import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PlexusBackground from "../components/PlexusBackground";
import CookieDisclaimer from "../components/CookieDisclaimer";
import SEO from "../components/SEO";
import { apiGet, formatProductForDisplay } from "../api/client";
import { useTwitchLive } from "../hooks/useTwitchLive";
import "../styles.css";
import { getRatingScoreColor } from "../utils/ratingColor";

export default function Home() {
  const { isLive, loading: twitchLoading } = useTwitchLive("zemaxi");
  const streamStatusLabel = twitchLoading
    ? "SYNC"
    : isLive
      ? "ONLINE"
      : "OFFLINE";
  const streamStatusClass = twitchLoading
    ? "animus-panel__status--sync"
    : isLive
      ? "animus-panel__status--online"
      : "animus-panel__status--offline";

  const [videos, setVideos] = useState([]);
  const [videosLoading, setVideosLoading] = useState(true);
  const [videosError, setVideosError] = useState(null);

  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState(null);

  const [latestReviews, setLatestReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await apiGet('/products?featured=1&limit=3');
        setFeaturedProducts(data.map(formatProductForDisplay));
      } catch (e) {
        console.error("Failed to fetch featured products:", e);
        setProductsError("Failed to load featured tools. Is the CMS API running?");
      } finally {
        setProductsLoading(false);
      }
    };

    const fetchVideos = async () => {
      try {
        const data = await apiGet('/videos?limit=3');
        setVideos(data);
      } catch (e) {
        console.error("Failed to fetch videos:", e);
        setVideosError(e.message);
      } finally {
        setVideosLoading(false);
      }
    };

    const fetchReviews = async () => {
      try {
        const data = await apiGet('/reviews?limit=3&excludeUpcoming=1');
        setLatestReviews(data);
      } catch (e) {
        console.error("Failed to fetch reviews:", e);
      } finally {
        setReviewsLoading(false);
      }
    };

    fetchProducts();
    fetchVideos();
    fetchReviews();
  }, []);

  return (
    <>
      <SEO
        title="Home"
        description="Welcome to ZeMaxi's digital hub! Watch live Twitch streams, explore free streaming tools for OBS Studio and Streamer.bot, and discover content that will elevate your streaming setup."
        keywords="ZeMaxi, Twitch streamer, streaming tools, OBS overlays, Streamer.bot, free stream tools, gaming streamer, live stream, content creator"
        url="https://zemaxi.com/"
      />
      <Navbar />
      <CookieDisclaimer />
      <main>
        <section className="hero">
          <div className="container hero-layout">
            <div className="hero-content">
              <p className="animus-hero-id font-mono">{'// NODE_ZEMAXI'}</p>
              <h1>ZEMAXI: <span className="highlight">Streamer & Tool Creator</span></h1>
              <p>Welcome to my digital hub for streaming entertainment and powerful tools for content creators. Explore my Twitch streams, YouTube content, and discover tools that will take your streaming setup to the next level.</p>
              <div className="hero-buttons">
                <a href="https://twitch.tv/zemaxi" target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-twitch"><i className="fab fa-twitch"></i> Watch on Twitch</a>
                <a href="https://youtube.com/@zemaxi/live" target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-youtube"><i className="fab fa-youtube"></i> Watch on YouTube</a>
                <Link to="/tools" className="btn btn-secondary">
                  <i className="fas fa-wrench"></i> Browse Stream Tools
                </Link>
              </div>
            </div>
            <aside className="animus-panel hero-status-panel" aria-label="System status">
              <div className="animus-panel__header font-mono">{'// SYSTEM_READOUT'}</div>
              <dl className="animus-panel__data font-mono">
                <div><dt>NODE</dt><dd>ZEMAXI</dd></div>
                <div><dt>TYPE</dt><dd>STREAM HUB</dd></div>
                <div>
                  <dt>STATUS</dt>
                  <dd className={streamStatusClass}>{streamStatusLabel}</dd>
                </div>
                <div><dt>CHANNELS</dt><dd>TWITCH / YOUTUBE</dd></div>
                <div><dt>TOOLS</dt><dd>OBS / STREAMERBOT</dd></div>
              </dl>
            </aside>
          </div>
          <div className="hero-background">
            <div className="grid"></div>
            <PlexusBackground />
          </div>
        </section>

        <section className="featured-products">
          <div className="container">
            <h2>Featured <span className="highlight">Tools</span></h2>
            <div className="product-grid">
              {productsLoading ? (
                <div className="shop-loading">
                  <div className="spinner"></div>
                  <p>Loading tools...</p>
                </div>
              ) : productsError ? (
                <div className="shop-error">
                  <p>{productsError}</p>
                </div>
              ) : (
                featuredProducts.map((product, idx) => (
                  <div className="product-card" key={product.id || idx}>
                    <div className="product-image">
                      <img src={product.img} alt={product.alt} />
                    </div>
                    <div className="product-info">
                      <div className={`product-tag ${product.tag}`}>{product.tag === "premium" ? "Premium" : product.tag.charAt(0).toUpperCase() + product.tag.slice(1)}</div>
                      <h3>{product.title}</h3>
                      <p>{product.description}</p>
                      {product.link && product.link.startsWith('/') ? (
                        <Link to={product.link} className={product.btnClass}>{product.btnText}</Link>
                      ) : (
                        <a href={product.link} className={product.btnClass} target="_blank" rel="noopener noreferrer">{product.btnText}</a>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="section-cta">
              <Link to="/tools" className="btn btn-secondary">Browse All Tools</Link>
            </div>
          </div>
        </section>

        <section className="latest-videos">
          <div className="container">
            <h2>Latest <span className="highlight">Videos</span></h2>
            <div className="video-grid" id="youtube-videos">
              {videosLoading && (
                <div className="video-loading">
                  <div className="spinner"></div>
                  <p>Loading latest videos...</p>
                </div>
              )}
              {videosError && (
                <div className="video-loading">
                  <p style={{ color: 'red' }}>Failed to load videos: {videosError}</p>
                </div>
              )}
              {!videosLoading && !videosError && videos.length === 0 && (
                <div className="video-loading">
                  <p>No videos found.</p>
                </div>
              )}
              {!videosLoading && !videosError && videos.map(video => (
                <div className="video-item" key={video.id.videoId}>
                  <a href={`https://www.youtube.com/watch?v=${video.id.videoId}`} target="_blank" rel="noopener noreferrer">
                    <div className="video-thumbnail">
                      <img src={video.snippet.thumbnails.high.url} alt={video.snippet.title} />
                    </div>
                    <div className="video-info">
                      <h3>{video.snippet.title}</h3>
                      <div className="video-meta">
                        <span>{new Date(video.snippet.publishedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </a>
                </div>
              ))}
            </div>
            <div className="section-cta">
              <Link to="/videos" className="btn btn-secondary">View All Videos</Link>
            </div>
          </div>
        </section>

        <section className="latest-reviews">
          <div className="container">
            <h2>Latest <span className="highlight">Reviews</span></h2>
            {reviewsLoading ? (
              <div className="video-loading">
                <div className="spinner"></div>
                <p>Loading reviews...</p>
              </div>
            ) : (
              <div className="reviews-grid">
                {latestReviews.map((game) => (
                  <div className="review-card" key={game.id}>
                    <div className="review-poster">
                      <img src={game.poster} alt={game.title} />
                      {game.rating != null && (
                        <div
                          className="review-rating"
                          style={{ borderColor: getRatingScoreColor(game.rating) }}
                        >
                          <span
                            className="rating-value"
                            style={{ color: getRatingScoreColor(game.rating) }}
                          >
                            {game.rating}
                          </span>
                          <span className="rating-max">/10</span>
                        </div>
                      )}
                    </div>
                    <div className="review-info">
                      <h3>{game.title}</h3>
                      <div className="review-meta">
                        <span className="review-year">{game.releaseYear}</span>
                        <span className="review-platform">{game.platform}</span>
                      </div>
                      <p className="review-excerpt">
                        {Array.isArray(game.review) && game.review[0]
                          ? `${game.review[0].substring(0, 150)}...`
                          : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="section-cta">
              <Link to="/reviews" className="btn btn-secondary">View All Reviews</Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
